import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const stats = [
  { label: 'Routes orchestrated / day', value: '6,200+', accent: 'text-cyan-300' },
  { label: 'Avg. delivery savings', value: '−18.4%', accent: 'text-emerald-300' },
  { label: 'Planning time reduced', value: '42 min', accent: 'text-amber-200' },
];

const capabilities = [
  {
    title: 'Autonomous Planning',
    description:
      'AI route composer generates hyper-efficient sequences across fleets, factoring traffic, service times, and SLAs in real time.',
    icon: '🛰️',
  },
  {
    title: 'Live Command Center',
    description:
      'Unified operations dashboard with live GPS overlays, predictive ETAs, and heartbeat health for every driver and order.',
    icon: '🧭',
  },
  {
    title: 'Optimization Intelligence',
    description:
      'Genetic, annealing, and reinforcement learners work in tandem—continuously re-optimizing against fresh signals.',
    icon: '🧠',
  },
  {
    title: 'Enterprise Guardrails',
    description:
      'SOC2-ready controls, multi-tenant isolation, audit trails, and custom policy engines designed for regulated industries.',
    icon: '🛡️',
  },
  {
    title: 'Predictive Insights',
    description:
      'Forecast volumes, capacity, and service risk using multi-objective ML trained on millions of historic legs.',
    icon: '🔮',
  },
  {
    title: 'Developer-first APIs',
    description:
      'GraphQL + REST endpoints with event streams, SDKs, and infrastructure-as-code deployment blueprints.',
    icon: '⚙️',
  },
];

const workflow = [
  {
    step: '01',
    title: 'Ingest',
    description:
      'Connect ERPs, TMS, spreadsheets, or live order streams. Our ML normalizes addresses, validates geos, and enriches constraints.',
  },
  {
    step: '02',
    title: 'Optimize',
    description:
      'Simulate millions of candidate networks in seconds. Blend heuristics and AI search tuned to your KPIs.',
  },
  {
    step: '03',
    title: 'Command',
    description:
      'Dispatch with one-click, monitor SLA drift, trigger playbooks, and automatically re-route when assumptions change.',
  },
];

const testimonials = [
  {
    quote:
      '“RouteForce cut our weekly planning cycle from six hours to under twenty minutes while improving on-time performance across 14 regions.”',
    author: 'Natalie Carter · VP Logistics, Northwind Food Group',
  },
  {
    quote:
      '“The predictive warnings have saved us from countless service misses. It feels like having an AI co-pilot embedded in operations.”',
    author: 'Jared Liu · Head of Delivery, Horizon Supply',
  },
];

const NodeMap = () => (
  <svg
    viewBox="0 0 520 520"
    xmlns="http://www.w3.org/2000/svg"
    className="h-full w-full"
  >
    <defs>
      <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.95" />
        <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
      </radialGradient>
    </defs>
    <g stroke="rgba(148, 163, 184, 0.25)" strokeWidth="1.2" fill="none">
      <path d="M60 120 Q180 40 260 120 T460 120" />
      <path d="M80 240 Q200 140 300 220 T480 240" />
      <path d="M60 360 Q200 460 320 360 T460 360" />
      <path d="M120 80 L80 240 L140 400" />
      <path d="M400 80 L440 240 L380 400" />
      <path d="M220 140 L260 240 L220 360" />
      <path d="M320 140 L280 240 L320 360" />
    </g>
    {[{ x: 70, y: 120 }, { x: 260, y: 120 }, { x: 450, y: 120 }, { x: 120, y: 80 }, { x: 420, y: 80 }, { x: 80, y: 240 }, { x: 260, y: 240 }, { x: 440, y: 240 }, { x: 140, y: 400 }, { x: 320, y: 360 }, { x: 420, y: 360 }].map((node, idx) => (
      <g key={idx}>
        <circle cx={node.x} cy={node.y} r={18} fill="url(#nodeGlow)" />
        <circle cx={node.x} cy={node.y} r={6.8} fill="#38f8ff" />
      </g>
    ))}
    <circle cx="260" cy="240" r="24" fill="rgba(248, 179, 25, 0.3)" />
    <circle cx="260" cy="240" r="9" fill="#f59e0b" />
  </svg>
);

const HomePage: React.FC = () => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 text-slate-100">
      <div className="hero-spark pointer-events-none absolute inset-0 opacity-70" />
      <motion.div
        className="pointer-events-none absolute -left-24 top-0 h-[36rem] w-[36rem] rounded-full bg-primary-500/30 blur-[140px]"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.45, 0.65, 0.45],
        }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="pointer-events-none absolute -bottom-48 right-[-10%] h-[44rem] w-[44rem] rounded-full bg-cyan-500/25 blur-[180px]"
        animate={{
          scale: [1.08, 0.95, 1.08],
          opacity: [0.35, 0.55, 0.35],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
      />

      <header className="relative z-20 py-10">
        <div className="shell flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4 text-lg font-semibold uppercase tracking-[0.35em] text-slate-300">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900/60 ring-1 ring-white/10">
              <span className="text-2xl">⚡</span>
            </div>
            <span>RouteForce Pro</span>
          </div>
          <nav className="flex flex-wrap items-center gap-4 text-sm font-medium text-slate-400 lg:gap-8">
            {['Platform', 'Solutions', 'Integrations', 'Enterprise'].map((item) => (
              <button
                key={item}
                type="button"
                className="rounded-full border border-white/10 px-5 py-2.5 tracking-[0.3em] uppercase transition hover:border-white/30 hover:text-white"
              >
                {item}
              </button>
            ))}
            <Link
              to="/auth"
              className="relative rounded-full bg-white/10 px-6 py-2.5 text-sm font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/20"
            >
              Launch App
            </Link>
          </nav>
        </div>
      </header>

      <main className="relative z-20 pb-28">
        <section className="py-10 md:py-16 lg:py-24">
          <div className="shell grid gap-16 lg:grid-cols-[minmax(0,_1.1fr)_minmax(0,_0.9fr)] lg:items-center">
            <div className="space-y-10">
              <motion.span
                className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-2 text-xs font-semibold uppercase tracking-[0.4em] text-slate-300"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                AI Logistics Control Tower
              </motion.span>

              <motion.h1
                className="text-balance text-[clamp(3.4rem,6vw,6rem)] font-semibold leading-[0.95]"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
              >
                Intelligent delivery networks that <span className="text-gradient-primary">self-optimise</span> in real time.
              </motion.h1>

              <motion.p
                className="max-w-2xl text-lg leading-relaxed text-slate-300 md:text-xl"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                RouteForce orchestrates millions of stops across fleets, balancing cost, sustainability, and service precision. Build a resilient logistics brain tuned to your enterprise.
              </motion.p>

              <motion.div
                className="flex flex-wrap items-center gap-4"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Link
                  to="/generate"
                  className="neon-border relative inline-flex items-center gap-3 rounded-full bg-primary-500/80 px-10 py-3 text-base font-semibold uppercase tracking-[0.35em] text-white transition hover:bg-primary-500"
                >
                  Generate Routes
                  <span aria-hidden className="text-lg">↗</span>
                </Link>
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-8 py-3 text-base font-semibold uppercase tracking-[0.35em] text-white transition hover:border-white/40 hover:bg-white/10"
                >
                  Live Dashboard
                  <span aria-hidden className="text-lg">●</span>
                </Link>
              </motion.div>

              <motion.dl
                className="grid gap-6 text-left sm:grid-cols-3"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.35 }}
                variants={{
                  hidden: {},
                  visible: {
                    transition: {
                      staggerChildren: 0.15,
                    },
                  },
                }}
              >
                {stats.map((stat) => (
                  <motion.div
                    key={stat.label}
                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                    className="glass rounded-3xl p-6"
                  >
                    <dt className="text-xs uppercase tracking-[0.4em] text-slate-400">{stat.label}</dt>
                    <dd className={`mt-3 text-3xl font-semibold ${stat.accent}`}>{stat.value}</dd>
                  </motion.div>
                ))}
              </motion.dl>
            </div>

            <motion.div
              className="relative aspect-square w-full max-w-[32rem] justify-self-center overflow-hidden rounded-[3rem] border border-white/10 bg-white/5 p-10 shadow-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="absolute inset-0 grid-lines opacity-40" />
              <div className="absolute left-10 top-10 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.4em] text-slate-200">
                Global Fleet Sync
              </div>
              <div className="absolute right-10 bottom-10 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-emerald-200">
                SLA 99.2%
              </div>
              <div className="relative z-10 h-full w-full">
                <NodeMap />
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-12">
          <div className="shell flex flex-wrap items-center justify-between gap-6 text-sm uppercase tracking-[0.3em] text-slate-400">
            {['Atlas Logistics', 'Northwind Supply', 'Acme Fresh', 'Skyline Drones', 'Chrono Express'].map((brand) => (
              <span key={brand} className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 rounded-full bg-white/40" />
                {brand}
              </span>
            ))}
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="shell grid gap-12 lg:grid-cols-[0.45fr_0.55fr] lg:items-start">
            <div className="sticky top-24 space-y-6">
              <span className="text-sm uppercase tracking-[0.35em] text-slate-400">How it works</span>
              <h2 className="text-[clamp(2.6rem,4vw,3.4rem)] font-semibold leading-tight text-white">
                Every delivery cycle becomes a self-learning system.
              </h2>
              <p className="text-lg text-slate-300">
                RouteForce ingests signals across your ecosystem in milliseconds, orchestrating the perfect route and keeping humans in control with clarity, not chaos.
              </p>
            </div>
            <div className="space-y-6">
              {workflow.map((item) => (
                <div key={item.title} className="glass relative overflow-hidden rounded-[2.5rem] p-8 md:p-10">
                  <div className="absolute right-8 top-8 text-5xl font-bold text-white/10">{item.step}</div>
                  <h3 className="text-2xl font-semibold text-white">{item.title}</h3>
                  <p className="mt-4 text-slate-300">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="shell space-y-12">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div>
                <span className="text-sm uppercase tracking-[0.35em] text-slate-400">Capabilities</span>
                <h2 className="mt-4 text-[clamp(2.4rem,3.7vw,3.5rem)] font-semibold text-white">
                  Built for enterprise velocity, designed for teams with taste.
                </h2>
              </div>
              <p className="max-w-2xl text-lg text-slate-300">
                Modular services stitched together with event streaming, zero-downtime deploys, and observability baked into every layer.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {capabilities.map((cap) => (
                <motion.div
                  key={cap.title}
                  whileHover={{ translateY: -6 }}
                  className="glass relative rounded-[2rem] p-8"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-2xl">
                    {cap.icon}
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold text-white">{cap.title}</h3>
                  <p className="mt-4 text-base text-slate-300">{cap.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="shell grid gap-10 lg:grid-cols-[0.55fr_0.45fr] lg:items-center">
            <div className="space-y-10">
              <span className="text-sm uppercase tracking-[0.35em] text-slate-400">Intelligence Core</span>
              <h2 className="text-[clamp(2.5rem,4vw,3.6rem)] font-semibold text-white">
                Observe, predict, and automate the entire last-mile command center.
              </h2>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  {
                    title: 'Observability Fabric',
                    description:
                      'Streaming metrics, anomaly detection, and replayable route timelines keep operations auditable.',
                  },
                  {
                    title: 'Policy Engine',
                    description:
                      'No-code governance for delivery windows, driver skills, sustainability targets, and compliance.',
                  },
                  {
                    title: 'Control APIs',
                    description:
                      'Drive playbooks via REST, GraphQL, or WebSocket events. Trigger re-optimisations and sync with ERPs.',
                  },
                  {
                    title: 'AI Copilots',
                    description:
                      'Natural-language insights and scenario sandboxes so teams can test strategies before they deploy.',
                  },
                ].map((item) => (
                  <div key={item.title} className="glass rounded-3xl p-6">
                    <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                    <p className="mt-3 text-sm text-slate-300">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="glass relative overflow-hidden rounded-[3rem] p-10">
              <div className="absolute inset-0 grid-lines opacity-30" />
              <div className="relative space-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm uppercase tracking-[0.35em] text-slate-400">Predictive outlook</span>
                  <span className="rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.4em] text-emerald-200">
                    Stable
                  </span>
                </div>
                <div className="space-y-5">
                  {[{ label: 'Carbon impact vs baseline', value: '−22%', tone: 'text-emerald-300' }, { label: 'Projected service risk', value: '1.4%', tone: 'text-amber-200' }, { label: 'Ops cost delta', value: '−$87k', tone: 'text-cyan-200' }].map((metric) => (
                    <div key={metric.label} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                      <span className="text-xs uppercase tracking-[0.35em] text-slate-400">{metric.label}</span>
                      <div className={`mt-3 text-3xl font-semibold ${metric.tone}`}>{metric.value}</div>
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-sm text-slate-300">
                  Live simulations run every <span className="text-white">47 seconds</span> to keep every route resilient.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="shell grid gap-12 lg:grid-cols-2">
            {testimonials.map((testimonial) => (
              <div key={testimonial.author} className="glass space-y-6 rounded-[2.5rem] p-10">
                <p className="text-2xl leading-snug text-white">{testimonial.quote}</p>
                <p className="text-sm uppercase tracking-[0.35em] text-slate-400">{testimonial.author}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-24 pt-10">
          <div className="shell-tight relative overflow-hidden rounded-[3rem] border border-white/10 bg-gradient-to-br from-primary-500/30 via-slate-900 to-primary-500/20 p-12 text-center shadow-glow">
            <motion.div
              className="pointer-events-none absolute -top-28 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-white/20 blur-[120px]"
              animate={{ opacity: [0.3, 0.6, 0.3], scale: [0.9, 1.1, 0.9] }}
              transition={{ duration: 9, repeat: Infinity }}
            />
            <div className="relative space-y-8">
              <h2 className="text-[clamp(2.6rem,4.2vw,3.6rem)] font-semibold text-white">
                Ready to ignite your delivery network?
              </h2>
              <p className="mx-auto max-w-3xl text-lg text-slate-200">
                Schedule a live blueprint session with our solution architects. We will model your territories, port your historic data, and hand you an AI logistics co-pilot built for 4K—and 8K—operations.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/auth"
                  className="inline-flex items-center gap-3 rounded-full bg-white px-10 py-3 text-base font-semibold uppercase tracking-[0.35em] text-slate-900 transition hover:bg-white/90"
                >
                  Book an Intelligent Demo
                  <span aria-hidden className="text-lg">→</span>
                </Link>
                <Link
                  to="/analytics"
                  className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-8 py-3 text-base font-semibold uppercase tracking-[0.35em] text-white transition hover:border-white/35 hover:bg-white/10"
                >
                  Explore analytics
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-20 pb-12">
        <div className="shell flex flex-col gap-4 text-xs uppercase tracking-[0.35em] text-slate-500 md:flex-row md:items-center md:justify-between">
          <span>© {new Date().getFullYear()} RouteForce. Precision logistics intelligence.</span>
          <div className="flex gap-4">
            <span>Security</span>
            <span>Status</span>
            <span>Documentation</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
